import { IPricingInfo } from "@entities/PricingTracker/lib/types";
import { Card, Flex, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { sumOf, safeDivide } from "@shared/lib/utils/mathHelper";
import { formatData } from "@shared/lib/utils/formatData";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { isNumber } from "@shared/lib/utils/isValueExists";
import { ICost } from "@shared/lib/types/briefInfluencerV2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface Props {
  data: IPricingInfo[];
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}

export enum ChartDataTypes {
  INFLUENCER_PRICE = "influencerPrice",
  BRAND_PRICE = "Brand Price",
  MARGIN = "Margin",
  ABSOLUTE_MARGIN_PERCENTAGE = "Absolute Margin Percentage",
  COMPANY_MARGIN = "Company Margin",
  COMPANY_MARGIN_PERCENTAGE = "Company Margin Percentage",
  INFLUENCER_NEGO_DIFFERENCE = "Influencer Nego Difference",
  ABSOLUTE_INFLUENCER_NEGO_PERCENTAGE = "Absolute Influencer Nego Percentage",
  ACTUAL_NUMBER_OF_INFLUENCER_NEGOS = "Actual Number of Influencer Negos",
}

type PeriodType = "daily" | "weekly" | "monthly" | "yearly";

export function ChartView({ data, dateRange }: Props) {
  const [periodType, setPeriodType] = useState<PeriodType>("monthly");

  const [chartType, setChartType] = useState<ChartDataTypes>(
    ChartDataTypes.MARGIN
  );

  const getDateKey = (date: dayjs.Dayjs, period: PeriodType): string => {
    switch (period) {
      case "daily":
        return date.format("DD MMM YYYY");
      case "weekly":
        return `${date.startOf("week").format("DD MMM")} - ${date
          .endOf("week")
          .format("DD MMM YYYY")}`;
      case "monthly":
        return date.format("MMM YYYY");
      case "yearly":
        return date.format("YYYY");
      default:
        return date.format("DD MMM YYYY");
    }
  };

  const getPeriodStart = (
    date: dayjs.Dayjs,
    period: PeriodType
  ): dayjs.Dayjs => {
    switch (period) {
      case "daily":
        return date.startOf("day");
      case "weekly":
        return date.startOf("week");
      case "monthly":
        return date.startOf("month");
      case "yearly":
        return date.startOf("year");
      default:
        return date.startOf("day");
    }
  };

  const getValueForRecord = (
    record: IPricingInfo,
    type: ChartDataTypes
  ): number => {
    switch (type) {
      case ChartDataTypes.INFLUENCER_PRICE:
        return isNumber(record.influencerCost?.cost)
          ? (record.influencerCost?.cost as number)
          : 0;
      case ChartDataTypes.BRAND_PRICE:
        return isNumber(record.brandCost?.cost)
          ? (record.brandCost?.cost as number)
          : 0;
      case ChartDataTypes.MARGIN:
        return isNumber(record.margin) ? (record.margin as number) : 0;
      case ChartDataTypes.ABSOLUTE_MARGIN_PERCENTAGE:
        return isNumber(record.marginPercentage)
          ? (record.marginPercentage as number)
          : 0;
      case ChartDataTypes.COMPANY_MARGIN: {
        const margin = isNumber(record.margin) ? (record.margin as number) : 0;
        const serviceCost = sumOf(
          record?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
        return margin + serviceCost;
      }
      case ChartDataTypes.COMPANY_MARGIN_PERCENTAGE: {
        const margin = isNumber(record.margin) ? (record.margin as number) : 0;
        const serviceCost = sumOf(
          record?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
        const brandCost = isNumber(record.brandCost?.cost)
          ? (record.brandCost?.cost as number)
          : 0;
        return safeDivide(margin + serviceCost, brandCost + serviceCost) * 100;
      }
      case ChartDataTypes.INFLUENCER_NEGO_DIFFERENCE:
        return isNumber(record.influencerNegoDifference)
          ? (record.influencerNegoDifference as number)
          : 0;
      case ChartDataTypes.ABSOLUTE_INFLUENCER_NEGO_PERCENTAGE:
        return isNumber(record.influencerNegoDifferencePercentage)
          ? (record.influencerNegoDifferencePercentage as number)
          : 0;
      case ChartDataTypes.ACTUAL_NUMBER_OF_INFLUENCER_NEGOS:
        return isNumber(record.finalInfluencerCost) &&
          isNumber(record.firstInfluencerCost)
          ? record.finalInfluencerCost - record.firstInfluencerCost !== 0
            ? 1
            : 0
          : 0;
      default:
        return 0;
    }
  };

  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Filter data by date range
    const filteredData = data.filter((record) => {
      if (!record.influencerCost?.createdAt) return false;
      const recordDate = dayjs(record.influencerCost.createdAt);
      if (dateRange[0] && recordDate.isBefore(dateRange[0], "day"))
        return false;
      if (dateRange[1] && recordDate.isAfter(dateRange[1], "day")) return false;
      return true;
    });

    // Group data by period
    const groupedData = new Map<string, IPricingInfo[]>();

    filteredData.forEach((record) => {
      if (!record.influencerCost?.createdAt) return;
      const recordDate = dayjs(record.influencerCost.createdAt);
      const periodStart = getPeriodStart(recordDate, periodType);
      const key = getDateKey(periodStart, periodType);

      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key)?.push(record);
    });

    // Sort periods chronologically
    const sortedKeys = Array.from(groupedData.keys()).sort((a, b) => {
      let dateA: dayjs.Dayjs;
      let dateB: dayjs.Dayjs;

      if (periodType === "weekly") {
        // Extract start date from range format "DD MMM - DD MMM YYYY"
        const startA = a.split(" - ")[0];
        dateA = dayjs(startA, "DD MMM");
        const startB = b.split(" - ")[0];
        dateB = dayjs(startB, "DD MMM");
      } else if (periodType === "monthly") {
        dateA = dayjs(a, "MMM YYYY");
        dateB = dayjs(b, "MMM YYYY");
      } else if (periodType === "yearly") {
        dateA = dayjs(a, "YYYY");
        dateB = dayjs(b, "YYYY");
      } else {
        dateA = dayjs(a, "DD MMM YYYY");
        dateB = dayjs(b, "DD MMM YYYY");
      }

      return dateA.valueOf() - dateB.valueOf();
    });

    // Calculate values for each period
    const values = sortedKeys.map((key) => {
      const records = groupedData.get(key) || [];

      if (chartType === ChartDataTypes.ACTUAL_NUMBER_OF_INFLUENCER_NEGOS) {
        // Count records with negotiations
        return records.filter(
          (r) =>
            isNumber(r.finalInfluencerCost) &&
            isNumber(r.firstInfluencerCost) &&
            r.finalInfluencerCost - r.firstInfluencerCost !== 0
        ).length;
      } else if (chartType === ChartDataTypes.ABSOLUTE_MARGIN_PERCENTAGE) {
        // Calculate from aggregated totals: (sum brandCost - sum influencerCost) / sum brandCost * 100
        const totalBrandCost = sumOf(
          records
            .filter((ele) => Boolean(ele))
            .map((ele) => ele?.brandCost as ICost),
          "cost"
        );
        const totalInfluencerCost = sumOf(
          records
            .filter((ele) => Boolean(ele))
            .map((ele) => ele?.influencerCost as ICost),
          "cost"
        );
        return (
          safeDivide(totalBrandCost - totalInfluencerCost, totalBrandCost) * 100
        );
      } else if (chartType === ChartDataTypes.COMPANY_MARGIN_PERCENTAGE) {
        // Calculate from aggregated totals: (sum margin + sum serviceCost) / (sum brandCost + sum serviceCost) * 100
        const totalMargin = sumOf(records, "margin");
        const totalServiceCost = sumOf(
          records
            .filter((ele) => Boolean(ele))
            .flatMap(
              (ele) =>
                ele?.brandLedger?.filter((ele) => ele?.type === "Debit") || []
            ),
          "serviceCost"
        );
        const totalBrandCost = sumOf(
          records
            .filter((ele) => Boolean(ele))
            .map((ele) => ele?.brandCost as ICost),
          "cost"
        );
        return (
          safeDivide(
            totalMargin + totalServiceCost,
            totalBrandCost + totalServiceCost
          ) * 100
        );
      } else if (
        chartType === ChartDataTypes.ABSOLUTE_INFLUENCER_NEGO_PERCENTAGE
      ) {
        // Calculate from aggregated totals: (sum finalInfluencerCost - sum firstInfluencerCost) / sum firstInfluencerCost * 100
        const totalFinalInfluencerCost = sumOf(records, "finalInfluencerCost");
        const totalFirstInfluencerCost = sumOf(records, "firstInfluencerCost");
        return (
          safeDivide(
            totalFinalInfluencerCost - totalFirstInfluencerCost,
            totalFirstInfluencerCost
          ) * 100
        );
      } else {
        // For absolute values, sum them up
        return records.reduce((acc, record) => {
          return acc + getValueForRecord(record, chartType);
        }, 0);
      }
    });

    return {
      labels: sortedKeys,
      datasets: [
        {
          label: chartType,
          data: values,
          borderColor: "#533AF5",
          backgroundColor: "rgba(83, 58, 245, 0.1)",
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [data, periodType, chartType, dateRange]);

  const formatYAxisValue = (value: number | string): string => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    if (
      chartType === ChartDataTypes.ABSOLUTE_MARGIN_PERCENTAGE ||
      chartType === ChartDataTypes.COMPANY_MARGIN_PERCENTAGE ||
      chartType === ChartDataTypes.ABSOLUTE_INFLUENCER_NEGO_PERCENTAGE
    ) {
      return `${numValue.toFixed(2)}%`;
    }

    return formatData(numValue, "a");
  };

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex gap={16} wrap="wrap">
          <Select
            prefix="Matrix:"
            style={{ minWidth: 200 }}
            placeholder="View Point"
            value={chartType}
            onChange={(value) => setChartType(value as ChartDataTypes)}
            options={Object.values(ChartDataTypes).map((type) => ({
              label: type,
              value: type,
            }))}
          />
          <Select
            prefix="Period Type:"
            style={{ minWidth: 150 }}
            placeholder="Period Type"
            value={periodType}
            onChange={(value) => setPeriodType(value as PeriodType)}
            options={[
              { label: "Daily", value: "daily" },
              { label: "Weekly", value: "weekly" },
              { label: "Monthly", value: "monthly" },
              { label: "Yearly", value: "yearly" },
            ]}
          />
        </Flex>

        {chartData.labels.length > 0 ? (
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  display: true,
                  position: "top" as const,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const value = context.parsed.y;
                      if (
                        chartType ===
                          ChartDataTypes.ABSOLUTE_MARGIN_PERCENTAGE ||
                        chartType ===
                          ChartDataTypes.COMPANY_MARGIN_PERCENTAGE ||
                        chartType ===
                          ChartDataTypes.ABSOLUTE_INFLUENCER_NEGO_PERCENTAGE
                      ) {
                        return `${context.dataset.label}: ${value?.toFixed(2)}%`;
                      }
                      return `${
                        context.dataset.label
                      }: ${value?.toLocaleString()}`;
                    },
                  },
                },
                datalabels: {
                  display: true,
                  anchor: "end" as const,
                  align: "left" as const,
                  offset: 8,
                  color: "#533AF5",
                  font: {
                    weight: "bold" as const,
                    size: 11,
                  },
                  formatter: (value: number) => {
                    return formatYAxisValue(value);
                  },
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (value) => formatYAxisValue(value as number),
                  },
                  grid: {
                    display: true,
                    color: "rgba(0, 0, 0, 0.05)",
                  },
                },
                x: {
                  grid: {
                    display: false,
                  },
                },
              },
            }}
            height={230}
          />
        ) : (
          <Flex
            justify="center"
            align="center"
            style={{ height: 400, color: "#999" }}
          >
            <Typography.Text type="secondary">
              No data available for the selected date range and filters
            </Typography.Text>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
